/**
 * Fetches live product pricing from the booking system's Supabase edge function.
 *
 * The `get-products` function combines Supabase product catalog with Stripe pricing.
 * Response is CDN-cached for 15min by the edge function itself.
 * We add a 5-min Next.js fetch cache on top.
 */

import { formatCurrency } from "@/lib/format"
import { getCountryConfig, getCountryConfigStatic } from "@/i18n/config"
import type { CmsServiceConfig } from "@/lib/cms"

const SUPABASE_URL =
  process.env.NEXT_PUBLIC_SUPABASE_URL ??
  "https://rnaixxwwvyedkujwijlp.supabase.co"
const SUPABASE_ANON_KEY =
  process.env.NEXT_PUBLIC_SUPABASE_ANON_KEY ??
  "eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6InJuYWl4eHd3dnllZGt1andpamxwIiwicm9sZSI6ImFub24iLCJpYXQiOjE3NjQxNjI2NzgsImV4cCI6MjA3OTUyMjY3OH0.muqVtDgZl00kV2e9hBtaI8vkTRPK5VrVpD9fOhgYU_o"

interface StripePrice {
  unit_amount: number
  currency: string
}

interface Product {
  id: string
  name: string
  product_type: "standard" | "package" | "addon"
  default_price?: StripePrice
  /** Member price from Stripe (get-products feed). Absent when the product has
   *  no distinct member price. This is the sole source of member pricing. */
  member_price?: StripePrice
  duration_minutes?: number
}

interface ProductsResponse {
  products: Product[]
  categories: unknown[]
  timestamp: string
  mode: string
}

/** Product name → price map key mapping */
const PRODUCT_PRICE_KEYS: Record<string, string> = {
  // Packages
  Core: "core",
  Advanced: "advanced",
  Pro: "pro",
  // Blood
  "Advanced blood test": "blood",
  // Imaging add-ons
  "Body composition": "addonBodyComposition",
  "Gender health (prostate or ovaries)": "addonGenderHealth",
  "Heart CT calcium scoring (with low-dose radiation)": "addonHeartCT",
  "Mamma MRI (with contrast dye)": "addonMammaMRI",
  "DEXA scan (low-dose radiation)": "addonDEXA",
  // Blood add-ons
  "Essential B-vitamins blood test": "addonBVitamins",
  "Omega-3 & 6 blood test": "addonOmega",
  "Hormone blood test (Women)": "addonHormoneWomen",
  "Hormone blood test (Men)": "addonHormoneMen",
  "Cortisol blood test": "addonCortisol",
  "Lipoprotein (a) blood test": "addonLpa",
  "PSA blood test": "addonPsa",
}

/**
 * TEST slug → price-map key bridge.
 *
 * The CMS control plane (`products`, since AHD-12802 phase 1) exposes a stable
 * Supabase `bookingProductId` per page-owning row, joined to the rest of the
 * site by the TEST SLUG — the `/tests/<slug>` path tail, which is what the keys
 * below are. The price map, in turn, is keyed by the stable price keys
 * (`blood`, `addonOmega`, …) every pricing consumer depends on. This table
 * bridges the two: given a control-plane row we know which price-map key its
 * Supabase product id should populate, so we can match a Stripe product by its
 * STABLE id instead of its mutable name.
 *
 * Packages (Core/Advanced/Pro) have no `services` row — they're matched by name
 * only, which is fine: `get-products` gates the whole response on the three
 * package names already, and finance doesn't rename the headline tiers casually.
 * If/when packages get CMS service rows, add them here.
 */
export const PRICE_KEY_FOR_SLUG: Record<string, string> = {
  "advanced-blood-test": "blood",
  "body-composition": "addonBodyComposition",
  "gender-health": "addonGenderHealth",
  "calcium-score": "addonHeartCT",
  "mamma-mri": "addonMammaMRI",
  "dexa-scan": "addonDEXA",
  "essential-b-vitamins-test": "addonBVitamins",
  "omega-blood-test": "addonOmega",
  "hormone-blood-test-women": "addonHormoneWomen",
  "hormone-blood-test-men": "addonHormoneMen",
  "cortisol-blood-test": "addonCortisol",
  lpa: "addonLpa",
  "psa-blood-test": "addonPsa",
}

/** CMS services config as passed to the price resolver: the map keyed by slug
 *  that `getServicesConfig()` returns, or the raw array. */
export type ServicesConfigInput =
  | ReadonlyMap<string, CmsServiceConfig>
  | readonly CmsServiceConfig[]

/**
 * Build a Supabase-product-id → price-map-key lookup from the CMS services
 * control plane. Lets `fetchPricesFromSupabase` resolve a Stripe product by its
 * stable `id` (rename-proof) instead of its display `name`. Only services that
 * have BOTH a populated `bookingProductId` AND a known price-map key (via
 * `PRICE_KEY_FOR_SLUG`) contribute; everything else is left to the name-match
 * fallback, so an unpopulated CMS field never breaks resolution.
 */
function buildIdToPriceKey(
  config: ServicesConfigInput | undefined
): Map<string, string> {
  const idToKey = new Map<string, string>()
  if (!config) return idToKey
  const rows = config instanceof Map ? Array.from(config.values()) : config
  for (const row of rows as readonly CmsServiceConfig[]) {
    const id = row.bookingProductId
    if (!id) continue
    const key = PRICE_KEY_FOR_SLUG[row.slug]
    if (!key) continue
    idToKey.set(id, key)
  }
  return idToKey
}

export interface PriceMap {
  /** Raw price amount without currency prefix (e.g. "1990" or "299") */
  [key: string]: string
}

/**
 * Member prices are piggybacked into the same `prices` / `fullPrices` maps under
 * a `member:`-prefixed key (nothing iterates these maps blindly, so this is
 * safe and avoids threading a parallel map through the whole quiz client tree).
 * `prices["member:addonOmega"]` = raw member amount; `fullPrices["member:…"]` =
 * the formatted label. Sourced solely from Stripe's `member_price`; absent for
 * products with no member price — today that is every EUR-market product, since
 * Stripe carries no EUR member prices yet. Read via the helpers in
 * `@/lib/pricing/member-prices`.
 */
export const memberPriceMapKey = (key: keyof PriceMap) => `member:${key}`

export interface PriceData {
  /** Prices formatted without currency (raw amount string) */
  prices: PriceMap
  /** Prices with localized currency formatting (e.g. "CHF 1'990.00" or "1.299,00 €") */
  fullPrices: PriceMap
  /** Currency code */
  currency: string
  /** Booking product UUID by price-map key (e.g. addonOmega → "fa77…"). Live from get-products. */
  productIds: PriceMap
}

/** Raw fallback prices in CHF (whole units, not cents) */
const FALLBACK_PRICES_CHF: Record<string, number> = {
  core: 1990,
  advanced: 2490,
  pro: 3549,
  blood: 299,
  addonBodyComposition: 100,
  addonGenderHealth: 150,
  addonHeartCT: 470,
  addonMammaMRI: 800,
  addonDEXA: 135,
  addonBVitamins: 49,
  addonOmega: 129,
  addonHormoneWomen: 149,
  addonHormoneMen: 149,
  // Single-marker add-ons. No EUR counterparts below: Stripe carries no
  // market-tagged EUR price for them, and inventing one is worse than hiding
  // the line (same reasoning as the imaging add-ons).
  addonCortisol: 29,
  addonPsa: 19,
  addonLpa: 29,
}

/**
 * Raw fallback prices in EUR (whole units, not cents) — the degradation net
 * for launched EUR markets when the live feed is unavailable, mirroring
 * FALLBACK_PRICES_CHF's role for CH. The live source is `get-products` with
 * the `market` param (Stripe prices tagged with the country's ISO-2 code).
 *
 * The figures match the market-tagged Stripe prices 1:1 (Advanced blood test
 * 299 €, B-vitamins 49 €, Omega-3 & 6 129 €, Hormones F/M 149 €). The MRI
 * packages + imaging add-ons are intentionally omitted: MRI is coming-soon on
 * the EUR markets (checkup phase), so those cards render a coming-soon state
 * with no price line — no need for an EUR figure until the MRI clinics go live.
 */
const FALLBACK_PRICES_EUR: Record<string, number> = {
  blood: 299,
  addonBVitamins: 49,
  addonOmega: 129,
  addonHormoneWomen: 149,
  addonHormoneMen: 149,
  // MRI packages + imaging add-ons stay deliberately absent. A 1:1 CHF→EUR
  // placeholder was tried and reverted: €2,490 is not worth CHF 2,490, and
  // publishing an invented figure on a market that can't book the product yet
  // is worse than admitting we don't know it. Consumers must treat a missing
  // key as "no authoritative EUR figure yet" and say so in the UI — the quiz
  // result renders a "price coming soon" label in the price slot, and
  // `mri-package-cards.tsx` renders its empty holding line.
  //
  // Populate these only alongside real EUR pricing sign-off, at which point the
  // labels disappear on their own.
}

/** Whether a country has authoritative pricing we can show. Any ENABLED market
 *  qualifies: a waitlist market advertises its real figures with waitlist CTAs
 *  (2026-07-29 decision — the blood check-up shows its price pre-launch so
 *  people know what they're joining the waitlist for), and `built` /
 *  `checkup-preview` previews render real prices behind the token gate.
 *  Products whose Stripe prices don't exist yet simply come back absent from
 *  the market-tagged feed and their price lines stay hidden. */
function hasAuthoritativePricing(country: string): boolean {
  return getCountryConfig(country) !== undefined
}

/**
 * Build a PriceData object from raw price numbers for a given country.
 *
 * Exported so client components can render correct currency + amounts on
 * the first paint (before `fetchPrices` resolves), instead of flashing CHF
 * figures on EUR-currency markets like /de/de.
 *
 * EUR markets that aren't ENABLED at all return empty `prices`/`fullPrices` so
 * consumers can hide the price line entirely rather than fall back to (a)
 * fabricated EUR figures, or (b) mislabel CHF amounts with €. Enabled markets
 * (waitlist included) get the EUR table below as the pre-feed seed.
 */
export function buildFallback(country: string): PriceData {
  const cfg = getCountryConfigStatic(country)
  const currency = cfg?.currency ?? "CHF"
  if (currency === "EUR" && !hasAuthoritativePricing(country)) {
    return { prices: {}, fullPrices: {}, currency, productIds: {} }
  }
  const raw = currency === "EUR" ? FALLBACK_PRICES_EUR : FALLBACK_PRICES_CHF
  const prices: PriceMap = {}
  const fullPrices: PriceMap = {}
  for (const [key, amount] of Object.entries(raw)) {
    prices[key] = String(amount)
    fullPrices[key] = formatCurrency(amount, country)
  }
  return { prices, fullPrices, currency, productIds: {} }
}

/** Format a Stripe cents amount as a localized currency string. */
function formatStripeAmount(unitAmount: number, country: string): string {
  return formatCurrency(unitAmount / 100, country)
}

/** Sync fallback for client UI (e.g. search popular rows) before network resolves. */
export function fallbackFullPrice(
  country: string | undefined,
  mapKey: keyof Pick<PriceMap, "core" | "blood">
): string {
  const fp = buildFallback(country ?? "ch").fullPrices[mapKey]
  return fp ?? ""
}

/**
 * Server-side fetch against the Supabase edge function. Used directly by
 * server components and proxied by `/api/prices` for client callers.
 *
 * Don't call this from a client component — it embeds the Supabase host in
 * the request, which is on common ad-blocker / extension denylists and was
 * the source of `TypeError: Failed to fetch` Sentry noise (issue 117794612).
 * Use `fetchPrices` instead, which routes through the same-origin proxy
 * when called in the browser.
 */
export async function fetchPricesFromSupabase(
  country: string,
  servicesConfig?: ServicesConfigInput
): Promise<PriceData> {
  const fallback = buildFallback(country)
  const cfg = getCountryConfigStatic(country)
  const currency = (cfg?.currency ?? "CHF").toUpperCase()

  // EUR markets that aren't enabled show no prices (see buildFallback) —
  // skip the fetch entirely.
  if (currency === "EUR" && !hasAuthoritativePricing(country)) {
    return fallback
  }

  try {
    const res = await fetch(`${SUPABASE_URL}/functions/v1/get-products`, {
      method: "POST",
      headers: {
        Authorization: `Bearer ${SUPABASE_ANON_KEY}`,
        "Content-Type": "application/json",
      },
      // `market` selects the country's Stripe prices: get-products resolves
      // prices tagged with the ISO-2 country code in Stripe price metadata and
      // omits products that carry no tagged price for that market. CH is the
      // default market (Stripe `default_price`); omitting the param there keeps
      // the edge function's legacy warm cache key. Next's fetch cache keys on
      // the body, so each market caches independently under the shared tag.
      body: JSON.stringify(
        country === "ch"
          ? { language: "en-US" }
          : { language: "en-US", market: country }
      ),
      // 24h ceiling, not a freshness target. The `prices` tag is busted
      // on demand: the CMS runs a change-detected hourly Stripe sync
      // (`/api/sync-prices` → `pricing-variables` rows), and any row it
      // actually writes fires the Payload afterChange hook → POST
      // /api/revalidate → `revalidateTag("prices")`. A real price change
      // therefore lands within the cron's hour, not this window.
      //
      // The old `300` was the binding constraint on ISR for every page that
      // renders a price server-side (~490 `tests/v/[code]/[slug]`
      // permutations plus pricing/membership/quiz/quiz-focus): Next takes the
      // MINIMUM fetch revalidate in a route as that route's regeneration
      // period, so the whole set regenerated every 5 minutes under traffic.
      // Client-rendered prices are unaffected either way — they read
      // `/api/prices`, which keeps its own `s-maxage=300` CDN window.
      next: { revalidate: 86400, tags: ["prices"] },
    })

    if (!res.ok) return fallback

    const data: ProductsResponse = await res.json()
    const prices: PriceMap = {}
    const fullPrices: PriceMap = {}
    const productIds: PriceMap = {}

    // Prefer the stable Supabase product id (from the CMS control plane) over
    // the mutable product name. A rename in Stripe/Supabase then can't silently
    // drop a price; the name map stays as the fallback for products with no CMS
    // `bookingProductId` populated (and for the headline packages, which have no
    // CMS service row).
    const idToKey = buildIdToPriceKey(servicesConfig)

    // Never let an amount denominated in another currency masquerade as this
    // market's — a CHF figure formatted with € would be silently wrong. Today
    // get-products omits untagged prices on non-default markets, so the
    // mismatch arm is pure defense.
    const inMarketCurrency = (price: StripePrice) =>
      price.currency.toUpperCase() === currency

    const assign = (key: string, product: Product) => {
      productIds[key] = product.id
      if (product.member_price && inMarketCurrency(product.member_price)) {
        const mk = memberPriceMapKey(key)
        prices[mk] = String(product.member_price.unit_amount / 100)
        fullPrices[mk] = formatStripeAmount(
          product.member_price.unit_amount,
          country
        )
      }
      if (!product.default_price || !inMarketCurrency(product.default_price))
        return
      const { unit_amount } = product.default_price
      prices[key] = String(unit_amount / 100)
      fullPrices[key] = formatStripeAmount(unit_amount, country)
    }

    // Pass 1: id-matches are authoritative — a stale duplicate that still
    // carries the old name must NOT later clobber a key already pinned by id.
    const claimed = new Set<string>()
    for (const product of data.products) {
      const key = idToKey.get(product.id)
      if (!key) continue
      assign(key, product)
      claimed.add(key)
    }
    // Pass 2: name-match fallback for any key not already claimed by an id.
    for (const product of data.products) {
      const key = PRODUCT_PRICE_KEYS[product.name]
      if (!key || claimed.has(key)) continue
      assign(key, product)
    }

    // Integrity gate, per market shape: CH sells the three headline packages,
    // so all three must resolve or we assume a broken feed and degrade to the
    // fallback. EUR markets don't necessarily sell packages at all (NL carries
    // no package prices in Stripe) — their headline product is the blood
    // check-up.
    const complete =
      currency === "EUR"
        ? Boolean(prices.blood)
        : Boolean(prices.core && prices.advanced && prices.pro)
    if (complete) {
      return { prices, fullPrices, currency, productIds }
    }

    return fallback
  } catch {
    return fallback
  }
}

/**
 * Fetch live prices. On the server, hits Supabase directly. In the browser,
 * goes through the same-origin `/api/prices` proxy so ad-blockers and
 * network filters that target the Supabase host can't drop the request.
 *
 * @param country - Country code (e.g. "ch", "nl", "de"). Launched EUR markets
 *   resolve their market-tagged Stripe prices; pre-launch EUR markets return
 *   empty maps so consumers hide the price line.
 * @param servicesConfig - CMS control plane from `products` (test slug → row,
 *   see `getServicesConfig`). When
 *   supplied on the server, prices/products resolve by stable Supabase id
 *   first, falling back to name-match. Ignored in the browser path, which
 *   proxies through `/api/prices` (that route loads the config server-side).
 */
export async function fetchPrices(
  country: string,
  servicesConfig?: ServicesConfigInput
): Promise<PriceData> {
  if (typeof window === "undefined") {
    return fetchPricesFromSupabase(country, servicesConfig)
  }

  const fallback = buildFallback(country)
  const cfg = getCountryConfigStatic(country)
  const currency = (cfg?.currency ?? "CHF").toUpperCase()

  // EUR markets that aren't enabled show no prices — same skip as the server.
  if (currency === "EUR" && !hasAuthoritativePricing(country)) {
    return fallback
  }

  try {
    const res = await fetch(
      `/api/prices?country=${encodeURIComponent(country)}`
    )
    if (!res.ok) return fallback
    return (await res.json()) as PriceData
  } catch {
    return fallback
  }
}
